// Reporting timezone selection.
//
// Two kinds of entry, deliberately kept distinct because they give different
// numbers for most of the year:
//
//   group 'fixed' -> never shifts (the affiliate-network convention). "EST"
//                    here means UTC-05:00 all year round. Sent as ?tz_offset=
//   group 'local' -> real local time, follows daylight saving. New York is
//                    EST in winter and EDT in summer. Sent as ?tz_name=
//
// The backend defaults to +120 (Israel) when neither param is present, so the
// default entry below must stay a fixed +120 to preserve existing behaviour.

export const DEFAULT_TZ_ID = 'israel_fixed'

const TZ_STORAGE_KEY = 'tz_offset'

export const TZ_GROUPS = [
    { key: 'fixed', heading: 'Fixed offset (no DST)' },
    { key: 'local', heading: 'Local time (with DST)' },
]

export const TIMEZONES = [
    // --- fixed offsets: never shift ---
    { id: 'israel_fixed', group: 'fixed', short: 'Israel', label: 'Israel — GMT+02:00', minutes: 120 },
    { id: 'est', group: 'fixed', short: 'EST', label: 'EST — Eastern (UTC−05:00)', minutes: -300 },
    { id: 'cst', group: 'fixed', short: 'CST', label: 'CST — Central (UTC−06:00)', minutes: -360 },
    { id: 'mst', group: 'fixed', short: 'MST', label: 'MST — Mountain (UTC−07:00)', minutes: -420 },
    { id: 'pst', group: 'fixed', short: 'PST', label: 'PST — Pacific (UTC−08:00)', minutes: -480 },
    { id: 'india', group: 'fixed', short: 'India', label: 'India — IST (UTC+05:30)', minutes: 330 },
    { id: 'utc', group: 'fixed', short: 'UTC', label: 'UTC / GMT', minutes: 0 },

    // --- named zones: follow daylight saving ---
    { id: 'america_new_york', group: 'local', short: 'New York', label: 'New York — EST/EDT', zone: 'America/New_York' },
    { id: 'america_chicago', group: 'local', short: 'Chicago', label: 'Chicago — CST/CDT', zone: 'America/Chicago' },
    { id: 'america_denver', group: 'local', short: 'Denver', label: 'Denver — MST/MDT', zone: 'America/Denver' },
    { id: 'america_los_angeles', group: 'local', short: 'Los Angeles', label: 'Los Angeles — PST/PDT', zone: 'America/Los_Angeles' },
    { id: 'europe_london', group: 'local', short: 'London', label: 'London — GMT/BST', zone: 'Europe/London' },
    { id: 'europe_berlin', group: 'local', short: 'Berlin', label: 'Berlin — CET/CEST', zone: 'Europe/Berlin' },
    { id: 'asia_jerusalem', group: 'local', short: 'Jerusalem', label: 'Jerusalem — IST/IDT', zone: 'Asia/Jerusalem' },
]

export const getZoneById = (id) => TIMEZONES.find(z => z.id === id)
    || TIMEZONES.find(z => z.id === DEFAULT_TZ_ID)

export const getZonesInGroup = (groupKey) => TIMEZONES.filter(z => z.group === groupKey)

// Anything missing or unrecognised (including a legacy numeric value from the
// previous offset-based version) falls back to the default.
export const getStoredTzId = () => {
    try {
        const stored = sessionStorage.getItem(TZ_STORAGE_KEY)
        return TIMEZONES.some(z => z.id === stored) ? stored : DEFAULT_TZ_ID
    } catch (e) {
        return DEFAULT_TZ_ID
    }
}

export const setStoredTzId = (id) => {
    try {
        sessionStorage.setItem(TZ_STORAGE_KEY, id)
    } catch (e) {
        // Private mode / storage disabled - redux still holds it for this view.
    }
}

// The single place the API contract lives. Components must not build this.
export const buildTzQueryParam = (zone) => zone.zone
    ? `&tz_name=${encodeURIComponent(zone.zone)}`
    : `&tz_offset=${zone.minutes}`

// Intl formatters are expensive to construct and the realtime chart formats up
// to 1440 points per poll, so cache one per zone.
const formatterCache = {}
const getFormatter = (zoneName, withSeconds) => {
    const key = `${zoneName}|${withSeconds}`
    if (!formatterCache[key]) {
        const options = { timeZone: zoneName, hourCycle: 'h23', hour: '2-digit', minute: '2-digit' }
        if (withSeconds) options.second = '2-digit'
        formatterCache[key] = new Intl.DateTimeFormat('en-GB', options)
    }
    return formatterCache[key]
}

const formatInZone = (date, zone, withSeconds) => {
    if (zone.zone) {
        return getFormatter(zone.zone, withSeconds).format(date)
    }
    // Fixed offset: shift the instant and read the UTC clock off it.
    const shifted = new Date(date.getTime() + zone.minutes * 60 * 1000)
    return shifted.toISOString().slice(11, withSeconds ? 19 : 16)
}

export const formatTimeInZone = (date, zone) => formatInZone(date, zone, true)

export const formatHourMinuteInZone = (date, zone) => formatInZone(date, zone, false)
